'use client';

import {createTheme} from "@mui/material";

const darkTheme = createTheme({
    spacing: (factor: number) => `${factor}rem`,
    shape: {
        borderRadius: 4,
    },
    palette: {
        mode: 'dark',
        background: {
            default: '#353434',
            paper: '#464444',
        },
        text: {
            primary: '#bababa',
            secondary: '#999999',
        },
        primary: {
            main: '#abd1c6',
        },
        secondary: {
            main: '#d3b976',
        },
        success: {
            main: '#1ea455',
        },
        warning: {
            main: '#dcb95c',
        },
        error: {
            main: '#c9664b',
        },
        info: {
            main: '#bcd9dd',
        },
        divider: '#001e1d',
        common: {
            black: '#ffffff',
            white: '#000000',
        },
        custom: {
            infoText: '#0c7f8b',
            inverted: '#333',
            beige: '#ebe7dd',
            errorMessage: '#d49f6b',
            link: '#18a7b5',
            hoverLink: '#0c7f8b',
            mustard: '#d3b976',
            border: 'rgba(171, 209, 198, 0.5)',
            bgTooltip: '#eeeeee',
            darkGray: '#b8a07b',
            slateGray: '#afafaf',
            camel: '#a59368',
            ultraLightGray: '#4a4a4a',
            lightGray: 'rgb(44 44 44 / 50%)',
            middleGray: 'rgba(45, 45, 45, 1)',
            mistyGray: '#c2c2c2c7',
            backgroundCoverImage: 'linear-gradient(to bottom, #2b2b2b, #121212)',
            borderCoverImage: 'linear-gradient(to bottom, #ad9267 0%, #aa8d61 100%)',
            borderGradient: 'linear-gradient(to bottom,  #ad9267 0%, #aa8d61 100%)',
            brownStripGradient: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
            brown: '#a6885a',
            darkBrown: '#382d12',
            hoverMainMenuColor: '#d1abf0',
            hoverMainMenuBackgroundColor: 'rgba(140,137,137,0.3)',
            breadcrumbsText: '#0c7f8b',
            breadcrumbsLink: '#008997',
            backgroundLabelInpressColor: 'linear-gradient(to right, #ece6e0, #e3dad0)',
            borderLabelInpressColor: '#d3b976',
            backgroundPaginationBulletSlider: 'linear-gradient(to bottom, #ebe4dd, #e7dfd7, #dfd4ca)',
            borderPaginationBulletSlider: 'rgba(166,136,90,0.5)',
            backgroundArrowButtonSlider: 'linear-gradient(to bottom, #eee9e3, #ebe4dd, #e3dad0)',
            dividerBorderColor: '#55534d',
            dividerBorderBottomColor: 'rgba(0,0,0,0.5)',
            dividerBorderArchiveColor: 'rgba(72,70,65,0.5)',
            dividerBoxShadowArchiveColor: 'rgba(0,0,0,0.25)',
            paperBorderColor: 'rgba(99,96,89,0.5)',
            backgroundJournalHeader: '#201f1d',
            backgroundColorJournalMenu: '#0c7f8b',
            textColorJournalMenu: '#abd1c6',
            borderColorVolumeActiveArchive: '#352808',
            textColorVolumeActiveArchive: '#352808',
            borderColorVolumeArchive: '#000000',
            textColorVolumeArchive: '#897c5b',
            textColorHierarchy: '#788b8b',
            boxSummary: 'rgb(28 28 28 / 50%)',
            textSummary: '#8f8e8ec7',
            socialIconColor: 'rgb(69 122 130)',
            socialIconColorHover: 'rgb(74 189 205)',
            teal: '#abd1c6',
            homeSearchBg: "#1E1E1E",
            homeSearchBorder: "#444444",
            homeSearchFocusBorder: "#FFD700",
            homeSearchIconColor: "#FFD700",
            homeCardBackground: '#353434',
            homeSliderCardBackground: '#4c4c4c',
            subtitleText: '#D3B976',
            cardBackground: '#464444',
            cardBorder: '#55534d',
            homePostColor: '#999999',
            homePostDateColor: '#13bccd',
            hierarchyTitleColor: '#53a5b0c7',
            menuBackground: '#313030',
            backgroundGradient: 'rgb(34,34,34)',
        },
    },
    typography: {
        fontFamily: ['brieflands-regular','brieflands-bold','Abel','Nova','Vazir','IRANSansNe'].join(','),
        fontSize: 14,
        fs1: {fontSize: '2.5rem', fontWeight: 700},
        fs2: {fontSize: '2rem', fontWeight: 600},
        fs3: {fontSize: '1.75rem', fontWeight: 500},
        fs4: {fontSize: '1.5rem', fontWeight: 500},
        fs5: {fontSize: '1.25rem', fontWeight: 400},
        fs6: {fontSize: '1rem', fontWeight: 400},
        fs7: {fontSize: '0.875rem', fontWeight: 300},
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1200
        },
    },
    components: {
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#383737',
                    color: '#b8a07b',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    textDecoration: 'none',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: "bold",
                    padding: '.5rem'
                },
                body: {
                    padding: '.5rem'
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    borderBottom: "1px solid rgba(54, 54, 54, 0.2)",
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    "& th": {
                        fontWeight: "bold",
                        padding: '.5rem',
                    },
                },
            },
        },
        MuiTableBody: {
            styleOverrides: {
                root: {
                    "& tr:nth-of-type(odd)": {
                        backgroundColor: "#464444",
                    },
                    "& tr:nth-of-type(even)": {
                        backgroundColor: "transparent",
                    },
                },
            },
        },
    },
});

export default darkTheme;
